import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  WhatsappLogo, UploadSimple, CheckCircle, Warning, XCircle,
  Files, Info, FileArrowUp, Sparkle, MagnifyingGlassPlus, ImageBroken,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminWhatsAppImport — drop a WhatsApp chat-export .zip → auto-log receipts
 * and photos onto matching Work Orders.
 *
 * Route: /admin/whatsapp-import  (admin-gated)
 * Backend: POST /api/whatsapp/import-zip
 */
export default function AdminWhatsAppImport() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [pending, setPending] = useState([]);   // unmatched from earlier imports
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";

  const loadPending = async () => {
    try {
      const r = await axios.get(`${BASE}/api/whatsapp/unmatched`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setPending(r.data.unmatched || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPending(); }, []);

  const cancelUpload = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  // Native drag-drop — the whole page catches file drops so you can just
  // yank the zip in from your Downloads folder without hunting for the button.
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  useEffect(() => {
    const onDragEnter = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounter.current += 1;
      setDragging(true);
    };
    const onDragOver = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
    const onDragLeave = () => {
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); }
    };
    const onDrop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounter.current = 0;
      setDragging(false);
      upload(e.dataTransfer.files[0]);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (file) => {
    if (!file) return;
    // Android's WhatsApp export shares via content:// URIs which sometimes
    // strip the .zip extension. Accept zip-ish files by name, MIME, or size hint.
    const looksLikeZip =
      file.name.toLowerCase().endsWith(".zip") ||
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed" ||
      file.type === "application/octet-stream" ||   // Android fallback
      /whatsapp|chat/i.test(file.name);
    if (!looksLikeZip) {
      toast.error(`Must be a WhatsApp chat export .zip (got ${file.type || "unknown"})`);
      return;
    }
    if (file.size === 0) {
      toast.error("File is empty — try re-exporting from WhatsApp");
      return;
    }
    setUploading(true);
    setResult(null);
    setProgress(0);
    abortRef.current = new AbortController();
    try {
      // <50 MB → single-shot (faster). Bigger → chunked (bypasses ingress caps).
      const useChunked = file.size > 50 * 1024 * 1024;
      const r = useChunked ? await uploadChunked(file) : await uploadSingle(file);
      setResult(r);
      toast.success(`Auto-attached ${r.stats.copied} of ${r.stats.total} media file(s)`);
    } catch (e) {
      const isTimeout = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
      const isSize = e?.response?.status === 413;
      const isAborted = e.code === "ERR_CANCELED" || /aborted|canceled/i.test(e.message || "");
      if (isAborted) toast.info("Upload canceled");
      else if (isSize) toast.error("Export too large — please split by date range in WhatsApp.");
      else if (isTimeout) toast.error("Upload timed out. Try again — chunked upload will resume progress.");
      else {
        const detail = e?.response?.data?.detail || e.message || "Import failed";
        toast.error(`Import failed: ${detail}`);
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const uploadSingle = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await axios.post(`${BASE}/api/whatsapp/import-zip`, fd, {
      headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      timeout: 900000,
      signal: abortRef.current?.signal,
    });
    return r.data;
  };

  const uploadChunked = async (file) => {
    const CHUNK = 5 * 1024 * 1024;   // 5 MB slices sail through any ingress
    const totalChunks = Math.ceil(file.size / CHUNK);

    // Init
    const init = await axios.post(`${BASE}/api/whatsapp/import-init`, {},
      { headers: { Authorization: `Bearer ${token()}` }, timeout: 30000, signal: abortRef.current?.signal });
    const importId = init.data.import_id;

    // Ship chunks in order
    for (let i = 0; i < totalChunks; i++) {
      if (abortRef.current?.signal.aborted) throw new Error("Upload canceled");
      const start = i * CHUNK;
      const end = Math.min(start + CHUNK, file.size);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append("import_id", importId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(totalChunks));
      fd.append("chunk", blob, `chunk-${i}.bin`);
      await axios.post(`${BASE}/api/whatsapp/import-chunk`, fd, {
        headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
        timeout: 120000,
        signal: abortRef.current?.signal,
      });
      setProgress(Math.round(((i + 1) / totalChunks) * 95));  // save last 5% for finalize
    }

    // Finalize (this is where processing happens — can take a while)
    setProgress(96);
    const fin = await axios.post(`${BASE}/api/whatsapp/import-finalize`,
      { import_id: importId },
      { headers: { Authorization: `Bearer ${token()}` }, timeout: 900000, signal: abortRef.current?.signal });
    setProgress(100);
    return fin.data;
  };

  return (
    <AdminShell>
      {dragging && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center bg-emerald-500/20 backdrop-blur-sm border-8 border-dashed border-emerald-500"
          data-testid="drag-overlay">
          <div className="bg-white px-8 py-6 border-4 border-emerald-500 shadow-2xl text-center">
            <FileArrowUp weight="fill" size={64} className="mx-auto text-emerald-600"/>
            <div className="mt-3 font-black uppercase tracking-widest text-xl text-emerald-800">
              Drop WhatsApp .zip
            </div>
            <div className="mt-1 text-sm text-slate-600">We'll match every receipt to its Work Order</div>
          </div>
        </div>
      )}
      <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="whatsapp-import-page">
        <div className="flex items-center gap-3">
          <WhatsappLogo weight="fill" size={30} className="text-emerald-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              WhatsApp Receipts Import
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Drop a WhatsApp chat export → auto-log every receipt onto the right Work Order.
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border-2 border-slate-200 bg-white">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-slate-700">
              <Info weight="fill" size={14} className="text-emerald-600"/> How to export
            </div>
            <ol className="mt-2 text-xs text-slate-700 space-y-1 list-decimal list-inside">
              <li>Open the WhatsApp group chat where your techs send receipts</li>
              <li>Tap the chat name at the top → scroll down</li>
              <li>Tap <b>Export Chat</b> → choose <b>Attach Media</b> (critical!)</li>
              <li>Share the resulting .zip to yourself (email / AirDrop / Files)</li>
              <li>Drop the .zip into the box on the right →</li>
            </ol>
            <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 rounded">
              <b>Any size works.</b> Uploads over 50 MB are auto-chunked into 5 MB slices, so a 200 MB+ export goes through fine. Duplicates are skipped on re-import.
            </div>
          </div>
          <div className="p-4 border-2 border-slate-200 bg-white">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-slate-700">
              <Info weight="fill" size={14} className="text-emerald-600"/> How we match
            </div>
            <ul className="mt-2 text-xs text-slate-700 space-y-1 list-disc list-inside">
              <li>Caption contains a <b>WO#</b> → matched (best)</li>
              <li>Caption or nearby message contains a <b>property address</b> → matched</li>
              <li>Sender is a Field Radar worker → matched to their active WO</li>
              <li>Receipts (Home Depot, Lowe's, etc.) → land in <b>Receipts</b></li>
              <li>All other photos → land in <b>Before Photos</b></li>
              <li>Videos + files &gt; 50MB → skipped by default</li>
              <li>Re-imports are safe — duplicates auto-skip</li>
            </ul>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="wa-upload-btn"
            className={`w-full h-40 border-4 border-dashed ${uploading ? "border-slate-300 bg-slate-100" : "border-emerald-400 bg-emerald-50 hover:border-emerald-600 hover:bg-emerald-100"} flex flex-col items-center justify-center transition disabled:opacity-60`}
          >
            {uploading ? (
              <>
                <UploadSimple weight="bold" size={40} className="text-slate-500 animate-pulse"/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-slate-600">
                  {progress < 96 ? `Uploading… ${progress}%` : "Processing…"}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {progress < 96
                    ? "Large exports auto-chunk into 5 MB slices. Please leave this tab open."
                    : "Matching every media file to a Work Order — this can take a minute."}
                </div>
              </>
            ) : (
              <>
                <FileArrowUp weight="fill" size={44} className="text-emerald-600"/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-emerald-800">
                  Drop WhatsApp .zip here
                </div>
                <div className="mt-1 text-[11px] text-slate-600">or click to browse (max 500 MB)</div>
              </>
            )}
          </button>
          {uploading && (
            <button
              type="button"
              onClick={cancelUpload}
              data-testid="wa-cancel-btn"
              className="mt-2 w-full h-9 border-2 border-red-400 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] tracking-widest"
            >
              Cancel upload
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed,application/octet-stream,*/*"
            className="hidden"
            data-testid="wa-file-input"
            onChange={(e) => { if (e.target.files?.[0]) { upload(e.target.files[0]); e.target.value = ""; }}}
          />
        </div>

        {pending.length > 0 && (
          <div className="mt-6 p-4 border-2 border-amber-300 bg-amber-50" data-testid="wa-pending-panel">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-amber-800">
              <Warning weight="fill" size={14}/> {pending.length} file{pending.length === 1 ? "" : "s"} still waiting for a home
            </div>
            <div className="text-xs text-slate-700 mt-1">
              From earlier imports. Pick an address for each and we'll show every WO at that property. Or tap <b>AI-scan all images</b> — Claude Vision reads each photo and drops a one-line summary so you can triage by content instead of UUID.
            </div>
            <div className="mt-3">
              <UnmatchedList rows={pending}
                onAttached={(id) => setPending(p => p.filter(x => x.id !== id))}
                onListRefresh={loadPending}/>
            </div>
          </div>
        )}

        {result && <ResultsPanel result={result}/>}
      </div>
    </AdminShell>
  );
}


function ResultsPanel({ result }) {
  const [tab, setTab] = useState("matched");
  const [unmatched, setUnmatched] = useState(result.unmatched || []);
  const s = result.stats;

  const dismissRow = (id) => {
    setUnmatched(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div className="mt-6 space-y-3" data-testid="wa-results">
      <div className="p-4 border-2 border-slate-900 bg-white grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <Stat label="Total media" val={s.total} tone="slate"/>
        <Stat label="Auto-logged" val={s.copied} tone="emerald"/>
        <Stat label="Unmatched" val={unmatched.length} tone="amber"/>
        <Stat label="Skipped" val={result.skipped.length} tone="slate"/>
      </div>

      <div className="inline-flex border-2 border-slate-200 bg-slate-50">
        {[
          { k: "matched",   label: `Logged (${result.matched.length})`, icon: CheckCircle, color: "text-emerald-700" },
          { k: "unmatched", label: `Unmatched (${unmatched.length})`,   icon: Warning,     color: "text-amber-700"   },
          { k: "skipped",   label: `Skipped (${result.skipped.length})`, icon: XCircle,     color: "text-slate-500"   },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`wa-tab-${t.k}`}
            className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${tab === t.k ? "bg-slate-900 text-white" : `${t.color} hover:bg-slate-100`}`}>
            <t.icon size={12} weight="fill"/> {t.label}
          </button>
        ))}
      </div>

      {tab === "matched" && <MatchedList rows={result.matched}/>}
      {tab === "unmatched" && <UnmatchedList rows={unmatched} onAttached={dismissRow}/>}
      {tab === "skipped" && <SkippedList rows={result.skipped}/>}
    </div>
  );
}


function Stat({ label, val, tone }) {
  const tones = {
    slate: "text-slate-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  };
  return (
    <div>
      <div className={`text-2xl font-black tabular-nums ${tones[tone]}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}


function MatchedList({ rows }) {
  if (rows.length === 0) return <Empty msg="Nothing auto-logged yet."/>;
  return (
    <div className="divide-y-2 divide-slate-100 border-2 border-slate-200 bg-white" data-testid="wa-matched-list">
      {rows.map((r, i) => (
        <div key={i} className="p-3 flex items-start gap-3 text-sm">
          <CheckCircle weight="fill" size={16} className="text-emerald-600 flex-none mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[11px] text-slate-500 truncate">{r.filename}</div>
            {r.caption && <div className="text-[11px] text-slate-700 line-clamp-2 italic">"{r.caption}"</div>}
            <div className="mt-1 text-xs text-slate-700 flex flex-wrap items-center gap-2">
              {r.sender && <span className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest">{r.sender}</span>}
              <a href={`/admin/jobs/${r.wo_id}`} className="inline-flex items-center gap-1 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest hover:bg-emerald-100">
                → WO {r.wo_number || r.wo_id.slice(-4)}
              </a>
              <span className="text-slate-600 truncate">{r.wo_address}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">→ {r.target_field.replace("_", " ")}</span>
              {r.match_reason?.startsWith("addr") && (
                <span className="border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest">addr match</span>
              )}
              {r.match_reason?.startsWith("tech") && (
                <span className="border border-blue-300 bg-blue-50 text-blue-800 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest">tech match</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function UnmatchedList({ rows, onAttached, onListRefresh }) {
  const [bulkAI, setBulkAI] = useState(false);
  const [aiProgress, setAiProgress] = useState({ done: 0, total: 0 });
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";

  const runBulkSummarize = async () => {
    const targets = rows.filter(r => r.id && r.kind === "image" && !r.ai_summary);
    if (targets.length === 0) {
      toast.info("All image rows already have summaries — nice.");
      return;
    }
    if (!window.confirm(`Run AI summary on ${targets.length} images? Each costs ~1 credit.`)) return;
    setBulkAI(true);
    setAiProgress({ done: 0, total: targets.length });
    const queue = [...targets];
    let done = 0;
    const worker = async () => {
      while (queue.length > 0) {
        const r = queue.shift();
        if (!r) return;
        try {
          await axios.post(`${BASE}/api/whatsapp/unmatched/${r.id}/summarize`,
            {}, { headers: { Authorization: `Bearer ${token()}` } });
        } catch { /* one failure shouldn't stop the batch */ }
        done += 1;
        setAiProgress({ done, total: targets.length });
      }
    };
    // Concurrency 3 — Claude Vision is fast; don't hammer, don't crawl.
    await Promise.all([worker(), worker(), worker()]);
    toast.success(`AI summarized ${done} of ${targets.length} images`);
    setBulkAI(false);
    onListRefresh && onListRefresh();
  };

  if (rows.length === 0) return <Empty msg="Nothing left unmatched — nice work."/>;
  const nAIable = rows.filter(r => r.id && r.kind === "image" && !r.ai_summary).length;
  return (
    <div className="divide-y-2 divide-slate-100 border-2 border-amber-200 bg-white" data-testid="wa-unmatched-list">
      <div className="p-3 bg-amber-50 text-xs text-amber-900 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          Pick an address for each file — we'll list every WO at that property so you can attach it to the right one. Files are staged safely — no need to re-upload the .zip.
        </div>
        {nAIable > 0 && (
          <button onClick={runBulkSummarize} disabled={bulkAI}
            data-testid="wa-unmatched-bulk-ai-btn"
            className="h-8 px-3 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 rounded whitespace-nowrap">
            <Sparkle size={11} weight="fill"/>
            {bulkAI
              ? `AI scanning ${aiProgress.done}/${aiProgress.total}…`
              : `AI-scan all ${nAIable} images`}
          </button>
        )}
      </div>
      {rows.map((r, i) => (
        <UnmatchedRow key={r.id || i} row={r} onAttached={onAttached}/>
      ))}
    </div>
  );
}


function UnmatchedRow({ row, onAttached }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [addresses, setAddresses] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [wos, setWos] = useState(null);
  const [selectedWo, setSelectedWo] = useState("");
  const [loading, setLoading] = useState(false);
  const [attached, setAttached] = useState(null);
  const [aiSummary, setAiSummary] = useState(row.ai_summary || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);

  // Thumbnail: fetch the staged file as a blob and turn it into an
  // object URL so the browser can render it via <img>. We can't use a
  // plain <img src=…> because the preview endpoint requires the Bearer
  // token — <img> doesn't send auth headers. Docs/videos skip this and
  // show a placeholder icon instead.
  const isImage = (row.kind === "image") || (row.content_type || "").startsWith("image/");
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(() => {
    if (!row.id || !isImage) return;
    let revoked = false;
    let created = "";
    (async () => {
      try {
        const r = await axios.get(`${BASE}/api/whatsapp/unmatched/${row.id}/preview`,
          { ...auth(), responseType: "blob" });
        if (revoked) return;
        created = URL.createObjectURL(r.data);
        setBlobUrl(created);
      } catch {
        setThumbBroken(true);
      }
    })();
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  const runSummarize = async () => {
    if (aiLoading || !row.id) return;
    setAiLoading(true);
    try {
      const r = await axios.post(`${BASE}/api/whatsapp/unmatched/${row.id}/summarize`,
        {}, auth());
      setAiSummary(r.data.summary || "");
      if (!r.data.ok) toast.error(r.data.summary || "Summary unavailable");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI summary failed"));
    } finally { setAiLoading(false); }
  };

  const openPicker = async () => {
    if (addresses) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/api/whatsapp/property-picker`, auth());
      setAddresses(r.data.addresses || []);
    } catch (e) {
      toast.error("Couldn't load addresses");
    } finally { setLoading(false); }
  };

  const pickAddress = async (addr) => {
    setSelectedAddress(addr);
    setSelectedWo("");
    setWos(null);
    if (!addr) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/api/whatsapp/wos-at-address`,
        { ...auth(), params: { address: addr } });
      setWos(r.data.wos || []);
    } catch (e) {
      toast.error("Couldn't load WOs at that address");
    } finally { setLoading(false); }
  };

  const attach = async () => {
    if (!selectedWo || !row.id) return;
    setLoading(true);
    try {
      const r = await axios.post(`${BASE}/api/whatsapp/unmatched/${row.id}/attach`,
        { wo_id: selectedWo }, auth());
      setAttached({ wo_id: selectedWo, target: r.data.target_field });
      toast.success(`Attached to WO — ${r.data.target_field.replace("_", " ")}`);
      onAttached?.(row.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Attach failed"));
    } finally { setLoading(false); }
  };

  const dismiss = async () => {
    if (!row.id) return;
    if (!window.confirm("Dismiss this file? It won't show up again.")) return;
    setLoading(true);
    try {
      await axios.post(`${BASE}/api/whatsapp/unmatched/${row.id}/dismiss`, {}, auth());
      onAttached?.(row.id);   // same effect — remove from list
    } catch (e) {
      toast.error("Dismiss failed");
    } finally { setLoading(false); }
  };

  if (attached) {
    return (
      <div className="p-3 flex items-center gap-3 text-sm bg-emerald-50" data-testid={`row-${row.id}-attached`}>
        <CheckCircle weight="fill" size={16} className="text-emerald-600 flex-none"/>
        <span className="font-mono text-[11px] text-slate-700 truncate flex-1">{row.filename}</span>
        <a href={`/admin/jobs/${attached.wo_id}`} className="text-[10px] font-black uppercase tracking-widest text-emerald-800 hover:underline">
          Open WO →
        </a>
      </div>
    );
  }

  return (
    <div className="p-3" data-testid={`row-${row.id || row.filename}`}>
      <div className="flex items-start gap-3">
        {/* Thumbnail — click to open the full-size lightbox. Falls back
            to a broken-image icon if the storage fetch failed. */}
        {isImage ? (
          <button type="button"
            onClick={() => blobUrl && setLightbox(true)}
            data-testid={`thumb-${row.id}`}
            className="flex-none w-16 h-16 rounded overflow-hidden bg-slate-100 border-2 border-slate-200 hover:border-cyan-500 hover:shadow-md transition group relative">
            {blobUrl ? (
              <>
                <img src={blobUrl} alt="" className="w-full h-full object-cover"
                  onError={() => setThumbBroken(true)}/>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition">
                  <MagnifyingGlassPlus size={18} weight="fill" className="text-white opacity-0 group-hover:opacity-100"/>
                </div>
              </>
            ) : thumbBroken ? (
              <ImageBroken size={22} weight="regular" className="text-slate-400 m-auto mt-5"/>
            ) : (
              <div className="w-full h-full animate-pulse bg-slate-200"/>
            )}
          </button>
        ) : (
          <div className="flex-none w-16 h-16 rounded bg-slate-100 border-2 border-slate-200 flex items-center justify-center">
            <Files size={22} className="text-slate-400"/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] text-slate-500 truncate" title={row.filename}>{row.filename}</div>
              {row.caption && <div className="text-[11px] text-slate-700 italic line-clamp-2 mt-0.5">"{row.caption}"</div>}
              {/* AI-generated summary line — the whole point of this section.
                  Cheap Claude Vision call, cached per file so re-opens are free. */}
              {aiSummary ? (
                <div className="text-xs text-cyan-900 bg-cyan-50 border-l-2 border-cyan-500 pl-1.5 py-0.5 mt-1 flex items-start gap-1"
                  data-testid={`ai-summary-${row.id}`}>
                  <Sparkle weight="fill" size={11} className="text-cyan-600 flex-none mt-0.5"/>
                  <span className="leading-tight">{aiSummary}</span>
                </div>
              ) : row.id && isImage && (
                <button type="button"
                  onClick={runSummarize}
                  disabled={aiLoading}
                  data-testid={`ai-summarize-${row.id}`}
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-cyan-800 bg-cyan-50 hover:bg-cyan-100 border border-cyan-300 px-1.5 py-0.5 rounded disabled:opacity-50">
                  <Sparkle weight="fill" size={10}/>
                  {aiLoading ? "Reading…" : "What is this?"}
                </button>
              )}
              <div className="mt-0.5 text-[10px] text-slate-500">
                {row.sender && <span>from {row.sender} · </span>}
                {row.size ? `${(row.size / 1024).toFixed(0)} KB · ` : ""}
                {row.kind}
                {row.detached_from_wo_number && (
                  <span className="ml-1 text-red-700 font-bold">
                    · detached from WO#{row.detached_from_wo_number}
                  </span>
                )}
              </div>
            </div>
            {row.id && (
              <button onClick={dismiss} disabled={loading}
                data-testid={`dismiss-${row.id}`}
                className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-600 flex-none">
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
      {row.id && (
        <div className="mt-2 pl-[76px] flex items-center gap-2 flex-wrap">
          <select
            value={selectedAddress}
            onChange={(e) => pickAddress(e.target.value)}
            onFocus={openPicker}
            disabled={loading}
            data-testid={`address-picker-${row.id}`}
            className="h-8 px-2 border-2 border-slate-300 text-xs font-medium min-w-[200px] max-w-full"
          >
            <option value="">— Pick property address —</option>
            {(addresses || []).map(a => (
              <option key={a.address} value={a.address}>
                {a.address} {a.active > 0 ? `(${a.active} open)` : ""}
              </option>
            ))}
          </select>
          {selectedAddress && wos !== null && (
            <select
              value={selectedWo}
              onChange={(e) => setSelectedWo(e.target.value)}
              disabled={loading}
              data-testid={`wo-picker-${row.id}`}
              className="h-8 px-2 border-2 border-slate-300 text-xs font-medium min-w-[200px]"
            >
              <option value="">
                {wos.length === 0 ? "— No WOs at this address —" : "— Pick a WO —"}
              </option>
              {wos.map(w => (
                <option key={w.id} value={w.id}>
                  WO {w.work_order_number || w.id.slice(-4)}
                  {w.status ? ` · ${w.status}` : ""}
                  {w.title ? ` · ${w.title.slice(0, 30)}` : ""}
                </option>
              ))}
            </select>
          )}
          {selectedWo && (
            <button onClick={attach} disabled={loading}
              data-testid={`attach-btn-${row.id}`}
              className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <CheckCircle size={12} weight="fill"/> {loading ? "…" : "Attach"}
            </button>
          )}
          {loading && !selectedWo && <span className="text-[10px] text-slate-400">loading…</span>}
        </div>
      )}
      {!row.id && (
        <div className="mt-2 pl-6 text-[10px] text-amber-700">
          Staging failed — file couldn't be parked for manual attach. Re-import the zip to retry.
        </div>
      )}
      {/* Full-size lightbox — click the thumbnail to open. Any click on
          the backdrop closes it. Prevents the row's parent click from
          eating the close. */}
      {lightbox && blobUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
          data-testid={`lightbox-${row.id}`}>
          <img src={blobUrl} alt={row.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}/>
          <button onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl font-black">
            ×
          </button>
        </div>
      )}
    </div>
  );
}


function SkippedList({ rows }) {
  if (rows.length === 0) return <Empty msg="Nothing skipped."/>;
  return (
    <div className="divide-y divide-slate-100 border-2 border-slate-200 bg-white text-xs" data-testid="wa-skipped-list">
      {rows.map((r, i) => (
        <div key={i} className="p-2 flex items-center gap-3">
          <XCircle size={14} className="text-slate-400 flex-none"/>
          <span className="font-mono text-slate-600 truncate flex-1">{r.filename}</span>
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{r.reason}</span>
        </div>
      ))}
    </div>
  );
}


function Empty({ msg }) {
  return (
    <div className="p-8 text-center text-slate-400 text-sm border-2 border-dashed border-slate-200">
      <Files size={32} className="mx-auto mb-2 opacity-40"/>
      {msg}
    </div>
  );
}
